import { useState } from "react";
import { InputBox, Dropdown } from "@components/index";
import eyeOpen from "@assets/icon/eyeOpenIcon.svg";
import eyeClose from "@assets/icon/eyeCloseIcon.svg";
import { userRoles } from "@constants/userConstants";
import useAddUserFormData from "./addUserFormData";
import "./index.scss";

const AddUserForm = ({ id, initialData = {}, onSubmit, onCancel }) => {
  const {
    formData,
    isSubmitting,
    isEdit,
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,
    getFieldError,
    hasFieldError,
  } = useAddUserFormData({ id, initialData, onSubmit, onCancel });

  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="add-user-form-modal">
      {/* Modal Header */}
      <div className="add-user-form-modal__header">
        <h2>{isEdit ? "Edit User" : "Add New User"}</h2>
        <p>
          {isEdit ? "Update user information" : "Create a new user account"}
        </p>
      </div>

      {/* Modal Body */}
      <div className="add-user-form-modal__body">
        <form className="add-user-form-modal__form" onSubmit={handleSubmit}>
          {/* Row 1: User Role and Active/Inactive Checkbox in 1 row */}
          <div className="add-user-form-modal__form-row">
            {/* User Role */}
            <div className="add-user-form-modal__form-group">
              {!isEdit ? (
                <>
                  <Dropdown
                    label="User Role"
                    value={formData.role}
                    onChange={(value) =>
                      handleChange({ target: { name: "role", value } })
                    }
                    options={userRoles}
                    error={hasFieldError("role")}
                    helperText={getFieldError("role")}
                    required
                    fullWidth
                    className="add-user-form-dropdown"
                  />
                </>
              ) : (
                <>
                  <InputBox
                    value={
                      formData.role.charAt(0).toUpperCase() +
                      formData.role.slice(1)
                    }
                    disabled
                    fullWidth
                    className="add-user-form-disabled-input"
                  />
                </>
              )}
            </div>

            {/* Active/Inactive Toggle Buttons */}
            <div className="add-user-form-modal__form-group">
              {/* <label className="add-user-form-modal__label">Status</label> */}
              <div className="add-user-form-modal__status-toggle">
                <button
                  type="button"
                  onClick={() =>
                    handleChange({ target: { name: "isActive", value: true } })
                  }
                  className={`add-user-form-modal__status-button ${
                    formData.isActive
                      ? "add-user-form-modal__status-button--active"
                      : ""
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleChange({ target: { name: "isActive", value: false } })
                  }
                  className={`add-user-form-modal__status-button ${
                    !formData.isActive
                      ? "add-user-form-modal__status-button--active"
                      : ""
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: Full Name and Email in 1 row */}
          <div className="add-user-form-modal__form-row">
            <div className="add-user-form-modal__form-group">
              <InputBox
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter full name"
                error={hasFieldError("name")}
                helperText={getFieldError("name")}
                required
                fullWidth
                className="add-user-form-input"
              />
            </div>
            <div className="add-user-form-modal__form-group">
              <InputBox
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter email address"
                error={hasFieldError("email")}
                helperText={getFieldError("email")}
                required
                fullWidth
                className="add-user-form-input"
              />
            </div>
          </div>

          {/* Row 3: Phone Number and Password in 1 row */}
          <div className="add-user-form-modal__form-row">
            <div className="add-user-form-modal__form-group">
              <InputBox
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                pattern="[0-9]{10}"
                inputMode="numeric"
                error={hasFieldError("phone")}
                helperText={getFieldError("phone")}
                fullWidth
                className="add-user-form-input"
              />
            </div>
            <div className="add-user-form-modal__form-group">
              <InputBox
                label={isEdit ? "New Password" : "Password"}
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={isEdit ? "Enter new password" : "Enter password"}
                error={hasFieldError("password")}
                helperText={getFieldError("password")}
                required={!isEdit}
                fullWidth
                className="add-user-form-input"
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle-button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    tabIndex={-1}
                  >
                    <img
                      src={showPassword ? eyeClose : eyeOpen}
                      alt={showPassword ? "Hide password" : "Show password"}
                      className="eye-button"
                    />
                  </button>
                }
              />
            </div>
          </div>
        </form>
      </div>

      {/* Modal Actions */}
      <div className="add-user-form-modal__actions">
        <button
          type="button"
          onClick={handleCancel}
          className="add-user-form-modal__button add-user-form-modal__button--secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className={`add-user-form-modal__button add-user-form-modal__button--primary ${
            isSubmitting ? "add-user-form-modal__button--loading" : ""
          }`}
          disabled={isSubmitting}
        >
          {isSubmitting ? "" : isEdit ? "Update User" : "Add User"}
        </button>
      </div>
    </div>
  );
};

export default AddUserForm;
